import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import { ensureUserDoc, getUserProfile, isAdminUser, signOut as fbSignOut, watchAuth, type UserProfile } from "./firebase";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  /** False while an admin previews the site as a member (see previewTier). */
  isAdmin: boolean;
  /** Real admin, even while previewing: may switch the preview on and off. */
  canPreview: boolean;
  /** "View as member" (Dew, 2026-10-27): the tier an admin is looking through, or null. */
  previewTier: string | null;
  setPreviewTier: (tier: string | null) => void;
  signOut: () => Promise<void>;
  /** null while loading (or signed out). */
  profile: UserProfile | null;
  refreshProfile: () => Promise<void>;
  /** Name shown to classmates: AKA, else nickname, else the account name. */
  displayName: string;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Survives a reload within the tab only, so a forgotten preview never sticks for good.
  const [previewTier, setPreview] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem("previewTier");
    } catch {
      return null;
    }
  });
  const setPreviewTier = (tier: string | null) => {
    setPreview(tier);
    try {
      if (tier) sessionStorage.setItem("previewTier", tier);
      else sessionStorage.removeItem("previewTier");
    } catch {
      /* storage blocked: the preview still works until reload */
    }
  };

  useEffect(() => {
    const unsub = watchAuth(async (u) => {
      setUser(u);
      setProfile(null);
      setLoading(false);
      if (u) {
        await ensureUserDoc(u);
        setProfile(await getUserProfile(u.uid).catch(() => null));
      }
    });
    return unsub;
  }, []);

  const canPreview = isAdminUser(user);
  const value: AuthContextValue = {
    user,
    loading,
    isAdmin: canPreview && !previewTier,
    canPreview,
    previewTier: canPreview ? previewTier : null,
    setPreviewTier,
    signOut: fbSignOut,
    profile,
    refreshProfile: async () => {
      if (user) setProfile(await getUserProfile(user.uid));
    },
    displayName: profile?.aka || profile?.nickname || user?.displayName || "สมาชิก",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
